// POS 路由註冊表（pure data，可 unit-test）
// see architecture.md#L1141
//
// 單一事實來源：src/lib/router.js 直接讀這個檔。
// 新增路由只要改這裡、並在 src/main.js 確保對應 component 已 import。

export const ROUTES = Object.freeze([
  { path: '#/login',              component: 'pos-login' },
  // 公開 QR 點餐路由（無 auth guard）— 顧客掃 QR 進入
  { path: '#/order/:slug/:table', component: 'pos-customer-menu',   guard: 'public' },
  { path: '#/order/:slug/:table/checkout', component: 'pos-customer-checkout', guard: 'public' },
  // 公開訂位路由（W9 MVP 提早）
  { path: '#/reserve/:slug',      component: 'pos-customer-reservation', guard: 'public' },
  // 客人自助管理訂位（確認出席/取消，HMAC token 連結）
  { path: '#/rsv/:token',         component: 'pos-customer-reservation-manage', guard: 'public' },
  // 員工後台訂位清單
  { path: '#/reservations',       component: 'pos-reservations',    guard: 'authed' },
  // Owner 訂位設定（用餐時長 / 併桌組合）
  { path: '#/settings/reservation', component: 'pos-reservation-config', guard: 'role:owner' },
  { path: '#/dashboard',          component: 'pos-dashboard',       guard: 'authed' },
  { path: '#/pos',                component: 'pos-order-grid',      guard: 'authed' },
  { path: '#/orders',             component: 'pos-order-list',      guard: 'authed' },
  { path: '#/checkout',           component: 'pos-checkout',        guard: 'authed' },
  // 金流結果頁（LINE Pay confirm / 綠界 OrderResultURL redirect 落點）
  // public：Phase 1.5 顧客自助結帳付款人是客人手機（未登入）；頁面只顯示狀態文案，
  // 無敏感資料（真正的 paid 由 server webhook 驗章決定，本頁僅 UI 展示）
  { path: '#/checkout/result/:status', component: 'pos-checkout-result', guard: 'public' },
  { path: '#/menu',               component: 'pos-menu-editor',     guard: 'role:owner' },
  { path: '#/reports',            component: 'pos-reports',         guard: 'role:owner' },
  { path: '#/staff',              component: 'pos-staff-mgr',       guard: 'role:owner' },
  { path: '#/xiaohong',           component: 'pos-xiaohong-chat',   guard: 'authed' },
  { path: '#/xiaonuan',           component: 'pos-xiaonuan-reviewer', guard: 'authed' },
  { path: '#/settings',           component: 'pos-settings',        guard: 'role:owner' },
  { path: '#/settings/ai-keys',   component: 'pos-ai-keys-admin',   guard: 'role:owner' },
  { path: '#/settings/lighthouse', component: 'pos-lighthouse-redemption', guard: 'role:owner' },
  { path: '#/settings/qr',        component: 'pos-qr-settings',     guard: 'role:owner' },
  // 金流帳號申請教學（BYOK 商家自有帳號模式）
  { path: '#/settings/payment',   component: 'pos-payment-settings', guard: 'role:owner' },
  { path: '#/settings/stations',  component: 'pos-stations-settings', guard: 'role:owner' },
  // 排班（W11 MVP，owner 代勞模式 — employees 含薪資 RLS owner-only）
  { path: '#/scheduling',         component: 'pos-scheduling',      guard: 'role:owner' },
  { path: '#/settings/scheduling', component: 'pos-scheduling-settings', guard: 'role:owner' },
  { path: '#/settings/workflow',  component: 'pos-workflow-editor', guard: 'role:owner' },
  { path: '#/inventory',          component: 'pos-inventory',       guard: 'role:owner' },
  { path: '#/xiaocang',           component: 'pos-xiaocang-alerts', guard: 'authed' },
  // 員工端班表（W13 MVP — 資料由 RLS 篩，任何登入角色可進）
  { path: '#/my-shifts',          component: 'pos-my-shifts',       guard: 'authed' },
]);

/** 把 path 轉成 regex（支援未來的 `:id` 參數） */
function pathToPattern(path) {
  const escaped = path.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/:\w+/g, '[^/]+');
  return new RegExp(`^${escaped}$`);
}

export const ROUTE_TABLE = ROUTES.map((r) => ({ ...r, pattern: pathToPattern(r.path) }));

/**
 * 從 hash 找出對應的路由條目
 * Query string 不參與匹配（金流 redirect 會帶 ?order_id=...）
 * @param {string} hash 例：'#/pos' 或 '#/checkout/result/success?order_id=xxx'
 * @returns {object|null}
 */
export function matchRoute(hash) {
  const path = (hash || '').split('?')[0];
  return ROUTE_TABLE.find((r) => r.pattern.test(path)) ?? null;
}

/**
 * 讀 hash 內的 query 參數（hash router 專用；location.search 在 hash route 下是空的）
 * @param {string} hash 例：'#/checkout/result/success?order_id=xxx'
 * @returns {URLSearchParams}
 */
export function hashQuery(hash) {
  const idx = (hash || '').indexOf('?');
  return new URLSearchParams(idx >= 0 ? hash.slice(idx + 1) : '');
}

export const DEFAULT_ROUTE = '#/dashboard';
export const LOGIN_ROUTE = '#/login';
